import { put, select, takeEvery } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { isEmpty, isNil } from 'lodash';
import * as moment from 'moment';

import {
  loadPageEditorSateError,
  setPageEditorEditablePage,
  updatePageEditorStateFromExternalSource,
  loadPageEditorSate,
  savePageEditorEditablePage,
  cancelPageEditorEdit,
  setPageEditorEditablePageById,
  startPageEditorSaga,
  cancelPageEditorSaga,
  resetPageEditorState,
  updatePageEditorFirstVisitState,
} from './actions';
import { createPage, updatePage } from '../actions';

import PATHS from 'src/constants/paths';

import { getEditablePage, getEditablePageWordsCount } from './selectors';
import { makeGetPagesHistoryExistingPage } from '../selectors';
import { getSelectedDate } from '../PagesHistoryCalendar/selectors';

import { createCancellableSaga } from 'src/helpers';
import { IAction } from '../../../interfaces/IAction';
import { StorageNames } from '../../../constants/storageNames';

import { initialState } from './reducer';
import { getExpectedWordsCount, selectUserProfile } from '../../UserProfile/selectors';

import { getType } from 'typesafe-actions';
import { trackEvent } from '../../../utils/Analytics';
import { getFinishedWritingAt, getStartedWritingAt } from '../../../Models';

function* saveEditablePageSaga() {
  const pageData = yield select(getEditablePage);
  const wordsGoal = yield select(getExpectedWordsCount);
  const isNewPage = isNil(pageData.dateId);
  const selectedDate = yield select(getSelectedDate);
  const wordsCount = yield select(getEditablePageWordsCount);
  const userProfile = yield select(selectUserProfile);

  const startedWritingAt = getStartedWritingAt(pageData.writingIntervals);
  const finishedWritingAt = getFinishedWritingAt(pageData.writingIntervals);

  const text = pageData.text;
  const userId = userProfile.userInfo ? userProfile.userInfo.id : 0;

  let createdAt = pageData.createdAt;

  const updatedAt = moment().toISOString();

  let dateId = pageData.dateId;

  if (isNewPage) {
    dateId = yield select(getSelectedDate);
    createdAt = moment(selectedDate)
      .hour(12)
      .toISOString();
  }

  const payload = {
    userId,
    startedWritingAt: moment(startedWritingAt ? startedWritingAt : selectedDate).toISOString(),
    finishedWritingAt: moment(finishedWritingAt ? finishedWritingAt : selectedDate).toISOString(),
    text,
    dateId,
    wordsGoal,
    createdAt,
    updatedAt,
    isDeleted: false,
    writingIntervals: pageData.writingIntervals,
  };

  if (isNewPage) {
    yield put(createPage(payload));
  } else {
    yield put(updatePage(payload));
  }

  trackEvent('Page_saved', {
    words_count: wordsCount,
    editing: !isNewPage,
  });

  yield put(cancelPageEditorEdit());
}

function* setEditablePageSaga({ payload }: IAction) {
  const page = yield select(makeGetPagesHistoryExistingPage(payload));

  yield put(setPageEditorEditablePage(page));
}

function* loadPageEditorStateSaga() {
  try {
    const rawPageEditorState = localStorage.getItem(StorageNames.pageEditor);
    let parsedPageEditorState = JSON.parse(rawPageEditorState || '{}');

    if (isNil(parsedPageEditorState) || isEmpty(parsedPageEditorState)) {
      parsedPageEditorState = initialState;
    }

    yield put(updatePageEditorStateFromExternalSource(parsedPageEditorState));
    localStorage.removeItem(StorageNames.pageEditor);
  } catch (e) {
    yield put(loadPageEditorSateError(e.message));
  }
}

function* pageEditorSaga() {
  yield takeEvery([getType(setPageEditorEditablePageById)], setEditablePageSaga);
  yield takeEvery([getType(cancelPageEditorEdit)], function* cancelPageEditSaga() {
    yield put(resetPageEditorState());
    yield put(updatePageEditorFirstVisitState(false));
    yield put(push(PATHS.HOME));
  });
  yield takeEvery([getType(savePageEditorEditablePage)], saveEditablePageSaga);
  yield takeEvery([getType(loadPageEditorSate)], loadPageEditorStateSaga);
}

export default createCancellableSaga({
  START_SAGA_ACTION: getType(startPageEditorSaga),
  CANCEL_SAGA_ACTION: getType(cancelPageEditorSaga),
  cancellableTask: pageEditorSaga,
});



// WEBPACK FOOTER //
// ./src/containers/PagesHistory/PageEditor/saga.ts